import React, { CSSProperties, useState } from "react";
import { Button, Header, Icon, List, Modal, Segment, TextArea } from "semantic-ui-react";

const PRIMARY_COLOR = "#F382A0";
const INACTIVE_COLOR = "#B3B3B3";

const styles = {
  sectionHeader: {
    background: PRIMARY_COLOR,
    padding: "8px 13px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    margin: 0,
  } as CSSProperties,
  noteContent: {
    margin: 0,
    minHeight: "93px",
    maxHeight: "300px",
    overflowY: "auto",
    padding: "10px 9px",
    borderRadius: "0 0 5px 5px",
    borderColor: PRIMARY_COLOR,
  } as CSSProperties,
  center: { textAlign: "center" } as CSSProperties,
  noMargin: { margin: 0 } as CSSProperties,
  primaryButton: { backgroundColor: PRIMARY_COLOR, color: "white" } as CSSProperties,
};

type NoteSectionProps = {
  notes?: Array<{
    id: string;
    date: string;
    content: string;
    author: string;
    createdAt: string;
  }>;
  isCollapsed: boolean;
  onToggle: () => void;
  onSaveNote?: (content: string) => void;
  onUpdateNote?: (id: string, content: string) => void;
  onDeleteNote?: (id: string) => void;
};

const NoteSection: React.FC<NoteSectionProps> = (props) => {
  const notes = props.notes || [];

  const [noteModal, setNoteModal] = useState<{
    open: boolean;
    mode: "create" | "edit";
    id?: string;
    content: string;
  }>({ open: false, mode: "create", content: "" });
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    id?: string;
  }>({ open: false });

  const handleOpenNoteModal = (mode: "create" | "edit", id?: string, content?: string) => {
    setNoteModal({
      open: true,
      mode,
      id,
      content: content || "",
    });
  };

  const handleCloseNoteModal = () => {
    setNoteModal({ open: false, mode: "create", content: "" });
  };

  const handleSaveNote = () => {
    if (noteModal.mode === "create" && props.onSaveNote) {
      props.onSaveNote(noteModal.content);
    } else if (noteModal.mode === "edit" && noteModal.id && props.onUpdateNote) {
      props.onUpdateNote(noteModal.id, noteModal.content);
    }
    handleCloseNoteModal();
  };

  const handleOpenDeleteModal = (id: string) => {
    setDeleteModal({ open: true, id });
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal({ open: false });
  };

  const handleConfirmDelete = () => {
    if (deleteModal.id && props.onDeleteNote) {
      props.onDeleteNote(deleteModal.id);
    }
    handleCloseDeleteModal();
  };

  return (
    <>
      <Segment.Group style={{ marginBottom: "10px" }}>
        {/* Note Header */}
        <Segment inverted style={styles.sectionHeader} onClick={props.onToggle}>
          <Header as="h5" inverted style={{ margin: 0, fontSize: "13px" }}>
            Note {notes.length}/5
          </Header>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              icon
              // inverted
              size="mini"
              style={{
                padding: 0,
                margin: "0 10px 0 0",
                background: "transparent",
                color: "white",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenNoteModal("create");
              }}
            >
              <Icon name="plus" style={styles.noMargin} />
            </Button>
            <Icon name={props.isCollapsed ? "angle down" : "angle up"} style={{ margin: 0 }} />
          </div>
        </Segment>

        {/* Note Content */}
        {!props.isCollapsed && (
          <Segment style={styles.noteContent}>
            {notes.length === 0 ? (
              <div
                style={{
                  ...styles.center,
                  color: INACTIVE_COLOR,
                  fontSize: "11px",
                  padding: "20px 0",
                }}
              >
                ไม่มี Note
              </div>
            ) : (
              <List divided relaxed>
                {notes.map((note) => (
                  <List.Item key={note.id}>
                    <div style={{ fontSize: "10px", marginBottom: "5px" }}>{note.date}</div>
                    <div style={{ fontSize: "10px", marginBottom: "8px" }}>{note.content}</div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ fontSize: "10px" }}>
                        <span style={{ color: INACTIVE_COLOR, marginRight: "5px" }}>
                          {note.createdAt}
                        </span>
                        <span style={{ fontWeight: 500 }}>{note.author}</span>
                      </div>
                      <Button.Group size="mini">
                        <Button
                          icon="edit"
                          style={{
                            color: PRIMARY_COLOR,
                            background: "transparent",
                            padding: "5px",
                          }}
                          onClick={() => handleOpenNoteModal("edit", note.id, note.content)}
                        />
                        <Button
                          icon="trash"
                          style={{
                            color: PRIMARY_COLOR,
                            background: "transparent",
                            padding: "5px",
                          }}
                          onClick={() => handleOpenDeleteModal(note.id)}
                        />
                      </Button.Group>
                    </div>
                  </List.Item>
                ))}
              </List>
            )}
          </Segment>
        )}
      </Segment.Group>

      {/* Note Modal */}
      <Modal open={noteModal.open} size="tiny" onClose={handleCloseNoteModal}>
        <Modal.Header>{noteModal.mode === "create" ? "Add Note" : "Edit Note"}</Modal.Header>
        <Modal.Content>
          <TextArea
            placeholder="กรอกข้อมูล Note..."
            value={noteModal.content}
            onChange={(e, { value }) => setNoteModal({ ...noteModal, content: value as string })}
            rows={8}
            style={{ width: "100%" }}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCloseNoteModal}>Cancel</Button>
          <Button style={styles.primaryButton} onClick={handleSaveNote}>
            Save
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={deleteModal.open} size="mini" onClose={handleCloseDeleteModal}>
        <Modal.Header>ยืนยันการลบ</Modal.Header>
        <Modal.Content>
          <p>คุณต้องการลบ Note นี้หรือไม่?</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCloseDeleteModal}>Cancel</Button>
          <Button style={styles.primaryButton} onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default NoteSection;
